/**
 * Phase 9 — Calendar (.ics) export.
 *
 * Pure helpers — produce an iCalendar 2.0 string from a list of games +
 * slots. UI can offer this as a download per team / competition.
 */

import type { Game, Iso8601, Rink, ScheduleSlot, Team, Tournament, Venue } from '../models';

/** Format a Date as the iCalendar UTC stamp. */
function fmt(d: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`;
}

function escapeText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

export interface BuildIcsInput {
  tournament: Tournament;
  games: ReadonlyArray<Game>;
  slots: ReadonlyArray<ScheduleSlot>;
  teams: ReadonlyArray<Team>;
  rinks: ReadonlyArray<Rink>;
  venues: ReadonlyArray<Venue>;
  /** When set, only games involving this team are emitted. */
  filterTeamId?: string;
  /** Calendar product id; surfaces in some clients. */
  prodId?: string;
}

export function buildIcs(input: BuildIcsInput): string {
  const { tournament, games, slots, teams, rinks, venues, filterTeamId } = input;
  const slotById = new Map(slots.map((s) => [s.id, s]));
  const teamById = new Map(teams.map((t) => [t.id, t]));
  const rinkById = new Map(rinks.map((r) => [r.id, r]));
  const venueById = new Map(venues.map((v) => [v.id, v]));
  const stampedAt = fmt(new Date());

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${input.prodId ?? 'The Scorekeeper'}//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(tournament.name)}`,
  ];

  for (const g of games) {
    if (filterTeamId && g.homeTeamId !== filterTeamId && g.awayTeamId !== filterTeamId) continue;
    if (!g.scheduleSlotId) continue;
    const slot = slotById.get(g.scheduleSlotId);
    if (!slot) continue;
    const start = parseIso(slot.startsAt);
    const end = parseIso(slot.endsAt);
    if (!start || !end) continue;
    const home = teamById.get(g.homeTeamId);
    const away = teamById.get(g.awayTeamId);
    const rink = rinkById.get(slot.rinkId);
    const venue = rink ? venueById.get(rink.venueId) : undefined;
    const summary = `${home?.name ?? 'Home'} vs ${away?.name ?? 'Away'}`;
    const locationParts = [rink?.name, venue?.name, venue?.address].filter(Boolean) as string[];
    lines.push(
      'BEGIN:VEVENT',
      `UID:${g.id}@thescorekeeper.ai`,
      `DTSTAMP:${stampedAt}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${escapeText(summary)}`,
      locationParts.length > 0 ? `LOCATION:${escapeText(locationParts.join(', '))}` : '',
      `DESCRIPTION:${escapeText(`${tournament.name} — ${g.displayLabel ?? summary}`)}`,
      'END:VEVENT',
    );
  }
  lines.push('END:VCALENDAR');
  return lines.filter(Boolean).join('\r\n');
}

function parseIso(s: Iso8601): Date | null {
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : t;
}

/** Trigger a download in the browser. No-op outside a browser context. */
export function downloadIcs(filename: string, ics: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
